/* eslint-disable camelcase */
import {
  InventoryLocationBuilding,
  InventoryLocationBuildingFloorplan,
  SerializedInventoryLocation,
} from '@enmon/commons/lib/tools';
import { useEffect, useState } from 'react';
import create from 'zustand';
import { captureException } from '@sentry/react';
import { SerializedMeter } from '@enmon/commons/lib/enmon';
import { getInventoryLocationBuildingFloorplanApi } from '../api/inventoryLocationBuildingFloorplan';
import { useOnlineStatus } from './useOnlineStatus';
import { isFloorplanValid, ValidatedFloorplan } from '../utils/isFloorplanValid';

export type Floorplan = Pick<InventoryLocationBuildingFloorplan, 'file_drive_id' | 'height' | 'width' | 'floor'> & {
  inv_loc_building: InventoryLocationBuilding;
};

export interface FloorplanStore {
  floorplan: FloorplanShape['floorplan'];
  setFloorplan: (payload: FloorplanShape['floorplan']) => void;
}

const useFloorplanStore = create<FloorplanStore>(set => ({
  floorplan: null,
  setFloorplan: (payload: FloorplanShape['floorplan']) =>
    set(() => ({
      floorplan: payload,
    })),
}));

export interface FloorplanWithBlob extends ValidatedFloorplan {
  blob: Blob;
}

const getFloorplanWithBlob = async (
  buildingFloor: NonNullable<SerializedMeter['buildingFloor']>,
  locationBuildingId?: InventoryLocationBuilding['id'],
  locationId?: SerializedInventoryLocation['id'],
): Promise<null | FloorplanWithBlob> => {
  const { data: [floorplan] = [] } = await getInventoryLocationBuildingFloorplanApi().find({
    floor: buildingFloor,
    inv_loc_building: { id: locationBuildingId },
    inv_location: { id: locationId },
  });

  if (!isFloorplanValid(floorplan)) return null;

  return {
    ...floorplan,
    blob: await getInventoryLocationBuildingFloorplanApi().getContent(floorplan.id),
  };
};

interface FloorplanShape {
  floorplan: FloorplanWithBlob | null;
  loading: boolean;
}

export const useFloorplan = (
  buildingFloor: NonNullable<SerializedMeter['buildingFloor']>,
  locationBuildingId?: InventoryLocationBuilding['id'],
  locationId?: SerializedInventoryLocation['id'],
): FloorplanShape => {
  const { floorplan: memoizedFloorplan, setFloorplan: setMemoizedFloorplan } = useFloorplanStore();
  const [loading, setLoading] = useState(false);

  const { isOnline } = useOnlineStatus();

  useEffect(() => {
    const getFloorplan = async () => {
      setLoading(true);
      try {
        const floorplanData = await getFloorplanWithBlob(buildingFloor, locationBuildingId, locationId);
        setMemoizedFloorplan(floorplanData);
        setLoading(false);
      } catch (error) {
        console.error(error);
        captureException(error);
        setLoading(false);
      }
    };

    if (
      isOnline &&
      (!memoizedFloorplan ||
        memoizedFloorplan.floor !== buildingFloor ||
        memoizedFloorplan.inv_loc_building?.id !== locationBuildingId) &&
      buildingFloor !== undefined &&
      (locationBuildingId !== undefined || locationId !== undefined)
    ) {
      getFloorplan();
    }
  }, [buildingFloor, isOnline, locationBuildingId, memoizedFloorplan, setMemoizedFloorplan, locationId]);

  return { floorplan: memoizedFloorplan, loading };
};
